import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { parseStoredUser } from '../../../../utils/authSession'
import { closeProject, getProjectStats, listProjects, openProject, getProjectProfitability } from '../api/projectApi'
import { fetchOrgFilterOptions } from '../../common/pmOptionUtils'
import { fetchUserOptions } from '../../../../api/filterOptionApi'
import { appToast } from '../../../../components/AppToast'
import { useExportJob } from '../../../../hooks/useExportJob'
import { PROJECT_TYPE_OPTIONS } from '../constants/projectConstants'

const DEFAULT_PAGE_SIZE = 15

function toIsoDate(value) {
  return value.toISOString().slice(0, 10)
}

function buildDateRangeFromPreset(preset) {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  if (preset === 'this_month') {
    const start = new Date(year, month, 1)
    const end = new Date(year, month + 1, 0)
    return { fromDate: toIsoDate(start), toDate: toIsoDate(end) }
  }

  if (preset === 'this_year') {
    const start = new Date(year, 0, 1)
    const end = new Date(year, 11, 31)
    return { fromDate: toIsoDate(start), toDate: toIsoDate(end) }
  }

  if (preset === 'all_time') {
    return { fromDate: '1900-01-01', toDate: '9999-12-31' }
  }

  const quarter = Math.floor(month / 3)
  const start = new Date(year, quarter * 3, 1)
  const end = new Date(year, quarter * 3 + 3, 0)
  return { fromDate: toIsoDate(start), toDate: toIsoDate(end) }
}

function deriveProjectStatus(item) {
  const isArchived = Number(item?.is_archive || 0) === 1
  if (isArchived) return 'CLOSED'
  if (Number(item?.is_completed || 0) === 1) return 'COMPLETED'

  const rawStart = item?.start_date
  const start = rawStart ? new Date(rawStart) : null
  if (!start || Number.isNaN(start.getTime())) return 'DRAFT'

  const today = new Date()
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  return startMid <= todayMid ? 'IN_PROGRESS' : 'DRAFT'
}

function toDisplayDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
}

function statusChipClass(status) {
  if (status === 'IN_PROGRESS') return 'text-emerald-700'
  if (status === 'CLOSED') return 'text-outline'
  if (status === 'COMPLETED') return 'text-primary'
  return 'text-on-surface-variant'
}

function getDaysRemainingText(endDate, status) {
  if (status === 'CLOSED' || status === 'COMPLETED') return 'COMPLETED'
  if (!endDate) return 'OPEN'

  const end = new Date(endDate)
  if (Number.isNaN(end.getTime())) return '-'

  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const diffMs = end.getTime() - startOfToday.getTime()
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (days <= 0) return 'DUE'
  return `${days} DAYS REMAINING`
}

export function useProjectListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchField = searchParams.get('search_field') || 'Name'
  const searchText = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || 'All'
  const portfolioFilter = searchParams.get('portfolio') || 'active'
  const projectTypeFilter = searchParams.get('project_type') || 'all'
  const billableFilter = searchParams.get('billable') || 'any'
  const companyFilter = searchParams.get('company_id') || 'All'
  const branchFilter = searchParams.get('branch_id') || 'All'
  const departmentFilter = searchParams.get('department_id') || 'All'
  const leadFilter = searchParams.get('lead_id') || 'All'
  const priorityFilter = searchParams.get('priority') || 'All'
  const billingModeFilter = searchParams.get('billing_mode') || 'All'
  const fromDateParam = searchParams.get('from_date') || ''
  const toDateParam = searchParams.get('to_date') || ''
  const datePreset = searchParams.get('date_preset') || 'this_quarter'
  const page = Number(searchParams.get('page') || 1)

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshTick, setRefreshTick] = useState(0)
  const [actionBusyId, setActionBusyId] = useState(null)
  const [profitabilityMap, setProfitabilityMap] = useState({})
  const [profitabilityLoadingMap, setProfitabilityLoadingMap] = useState({})

  // ─ Universal Background Job Export ──────────────────────────────────────
  const { triggerExport, isExporting } = useExportJob({ module: 'projects' })
  const [kpiStats, setKpiStats] = useState({
    activeProjects: 0,
    activeBillableProjects: 0,
    trendPctVsLastQuarter: 0,
    totalBillableAmount: 0,
    totalReceivedAmount: 0,
    billingFillPct: 0,
  })
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const [leadOptions, setLeadOptions] = useState([{ value: 'All', label: 'All Leads' }])

  useEffect(() => {
    let active = true
    async function loadMasterOptions() {
      try {
        const [orgOptions, userRes] = await Promise.allSettled([
          fetchOrgFilterOptions({ pageSize: 200 }),
          fetchUserOptions('', 1, 200),
        ])

        if (!active) return

        if (orgOptions.status === 'fulfilled') {
          setCompanyOptions(orgOptions.value.companyOptions)
          setBranchOptions(orgOptions.value.branchOptions)
          setDepartmentOptions(orgOptions.value.departmentOptions)
        }

        if (userRes.status === 'fulfilled') {
          const users = userRes.value?.items || []
          setLeadOptions([
            { value: 'All', label: 'All Leads' },
            ...users.map((u) => ({ value: String(u.id || u.value), label: u.name || u.label || u.email })),
          ])
        }
      } catch (err) {
        console.error('Failed to load master options for projects:', err)
      }
    }
    loadMasterOptions()
    return () => {
      active = false
    }
  }, [])

  const canEditProject = useMemo(() => {
    const user = parseStoredUser()
    const isAdmin = Boolean(user?.isAdmin || user?.is_admin)
    const privileges = Array.isArray(user?.privileges) ? user.privileges : []
    return isAdmin || privileges.includes('ProjectEdit')
  }, [])

  const canViewFinancials = useMemo(() => {
    const user = parseStoredUser()
    const isAdmin = Boolean(user?.isAdmin || user?.is_admin)
    const privileges = Array.isArray(user?.privileges) ? user.privileges : []
    return isAdmin || privileges.includes('ProjectFinancialsView')
  }, [])

  const dateRange = useMemo(() => {
    if (fromDateParam || toDateParam) {
      return { fromDate: fromDateParam, toDate: toDateParam }
    }
    return buildDateRangeFromPreset(datePreset)
  }, [datePreset, fromDateParam, toDateParam])

  const isArchivedMode = portfolioFilter === 'archived'

  const queryParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      includeTotal: true,
      ...(portfolioFilter === 'archived' ? { archive: true } : portfolioFilter === 'active' ? { archive: false } : {}),
      search: searchText.trim(),
      ...(dateRange.fromDate ? { fromDate: dateRange.fromDate } : {}),
      ...(dateRange.toDate ? { toDate: dateRange.toDate } : {}),
      ...(statusFilter !== 'All' && statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(projectTypeFilter && projectTypeFilter !== 'all' ? { project_type: projectTypeFilter } : {}),
      ...(billableFilter === 'billable' ? { billable: true } : billableFilter === 'non_billable' ? { billable: false } : {}),
      ...(companyFilter !== 'All' ? { company_id: Number(companyFilter) } : {}),
      ...(branchFilter !== 'All' ? { branch_id: Number(branchFilter) } : {}),
      ...(departmentFilter !== 'All' ? { department_id: Number(departmentFilter) } : {}),
      ...(leadFilter !== 'All' ? { lead_id: Number(leadFilter) } : {}),
      ...(priorityFilter !== 'All' ? { priority: priorityFilter } : {}),
      ...(billingModeFilter !== 'All' ? { billing_mode: billingModeFilter } : {}),
    }),
    [
      dateRange.fromDate,
      dateRange.toDate,
      page,
      pageSize,
      searchText,
      portfolioFilter,
      projectTypeFilter,
      statusFilter,
      billableFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      leadFilter,
      priorityFilter,
      billingModeFilter,
    ],
  )

  useEffect(() => {
    let active = true

    async function loadProjects() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listProjects(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load projects.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadProjects()
    return () => {
      active = false
    }
  }, [queryParams, page, pageSize, refreshTick])

  useEffect(() => {
    let active = true
    async function loadProjectStats() {
      try {
        const stats = await getProjectStats()
        if (!active) return
        setKpiStats({
          activeProjects: Number(stats?.activeProjects || 0),
          activeBillableProjects: Number(stats?.activeBillableProjects || 0),
          trendPctVsLastQuarter: Number(stats?.trendPctVsLastQuarter || 0),
          totalBillableAmount: Number(stats?.totalBillableAmount || 0),
          totalReceivedAmount: Number(stats?.totalReceivedAmount || 0),
          billingFillPct: Number(stats?.billingFillPct || 0),
        })
      } catch {
        if (!active) return
        setKpiStats({
          activeProjects: 0,
          activeBillableProjects: 0,
          trendPctVsLastQuarter: 0,
          totalBillableAmount: 0,
          totalReceivedAmount: 0,
          billingFillPct: 0,
        })
      }
    }
    loadProjectStats()
    return () => {
      active = false
    }
  }, [refreshTick])

  useEffect(() => {
    if (!canViewFinancials || items.length === 0) {
      setProfitabilityMap({})
      setProfitabilityLoadingMap({})
      return
    }

    let active = true
    const billableProjects = items.filter(item => Boolean(item.billable))

    // Set all billable projects to loading
    setProfitabilityLoadingMap(prev => {
      const next = { ...prev }
      billableProjects.forEach(item => {
        next[item.id] = true
      })
      return next
    })

    const today = new Date().toISOString().slice(0, 10)

    billableProjects.forEach(async (proj) => {
      try {
        // Metrics column always shows cumulative billing from project start to today,
        // independently of the page-level date filter (which only filters which projects appear).
        const fetchParams = {}
        if (proj.start_date) fetchParams.fromDate = String(proj.start_date).slice(0, 10)
        fetchParams.toDate = today
        const data = await getProjectProfitability(proj.id, fetchParams)

        if (!active) return
        setProfitabilityMap(prev => ({
          ...prev,
          [proj.id]: data || { rollup: { totalRevenue: 0, totalCost: 0, profitabilityAmount: 0 }, project: { totalRevenue: 0, totalCost: 0, profitabilityAmount: 0 } }
        }))
      } catch (err) {
        console.error(`Failed to load profitability for project ${proj.id}:`, err)
        if (!active) return
        setProfitabilityMap(prev => ({
          ...prev,
          [proj.id]: { rollup: { totalRevenue: 0, totalCost: 0, profitabilityAmount: 0 }, project: { totalRevenue: 0, totalCost: 0, profitabilityAmount: 0 } }
        }))
      } finally {
        if (active) {
          setProfitabilityLoadingMap(prev => ({
            ...prev,
            [proj.id]: false
          }))
        }
      }
    })

    return () => {
      active = false
    }
  }, [items, canViewFinancials])

  const rows = useMemo(() => {
    const mapped = items.map((item) => {
      const status = deriveProjectStatus(item)
      const budget = Number(item.budget || 0)
      const billed =
        item.total_received_amount !== undefined && item.total_received_amount !== null
          ? Number(item.total_received_amount || 0)
          : Number(item.received_amount || 0) + Number(item.advance_amount || 0)
      const delta = billed - budget
      const isArchived = Number(item.is_archive || 0) === 1

      return {
        id: Number(item.id),
        projectCode: item.project_code || '-',
        name: item.name || '-',
        managerName: item.manager_name || '-',
        status,
        statusClass: statusChipClass(status),
        isArchived,
        startDateText: toDisplayDate(item.start_date),
        endDateText: toDisplayDate(item.end_date),
        durationHint: getDaysRemainingText(item.end_date, status),
        billable: Boolean(item.billable),
        currency: item.project_amc_currency || 'INR',
        parentProjectId: item.parent_project_id ? Number(item.parent_project_id) : null,
        projectBillingMode: item.project_billing_mode || 'AMC',
        billingType: item.billing_type || null,
        costAmount: budget,
        billAmount: billed,
        deltaAmount: delta,
        deltaLabel: delta >= 0 ? 'SURPLUS' : 'DEFICIT',
        hasDateChanges: Boolean(item.hasDateChanges || (item.date_change_count && Number(item.date_change_count) > 0)),
      }
    })

    return mapped
  }, [items])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  const stats = useMemo(() => {
    const activeProjects = Number(kpiStats.activeProjects || 0)
    const activeBillableProjects = Number(kpiStats.activeBillableProjects || 0)

    // Total Billing KPI values come from the global /projects/stats API response,
    // which aggregates across ALL projects (not just the current page).
    // The per-project profitability breakdown in profitabilityMap is still used
    // for the individual table rows' Metrics column.
    let totalBillableAmount = 0
    let totalReceivedAmount = 0
    let billingFillPct = 0

    if (canViewFinancials) {
      totalBillableAmount = Number(kpiStats.totalBillableAmount || 0)
      totalReceivedAmount = Number(kpiStats.totalReceivedAmount || 0)
      billingFillPct = Number(kpiStats.billingFillPct || 0)
    }

    const currency = rows.length > 0 ? (rows.find(r => r.currency)?.currency || 'USD') : 'USD'

    return {
      activeProjects,
      activeBillableProjects,
      totalBillableAmount,
      totalReceivedAmount,
      billingFillPct,
      trendPct: Number(kpiStats.trendPctVsLastQuarter || 0),
      currency,
    }
  }, [rows, kpiStats, canViewFinancials])


  function updateParam(key, value, resetPage = true) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const cleanValue = key === 'search' ? String(value || '') : String(value || '').trim()
      
      if (cleanValue && cleanValue !== 'all' && cleanValue !== 'any' && cleanValue !== 'this_quarter' && cleanValue !== 'active') {
        next.set(key, cleanValue)
      } else {
        next.delete(key)
      }
      
      if (resetPage) {
        next.delete('page') // delete page to naturally default back to 1 instead of keeping ?page=1
      }
      return next
    }, { replace: true })
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextPage === 1) {
        next.delete('page')
      } else {
        next.set('page', String(nextPage))
      }
      return next
    }, { replace: true })
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      goToPage(page + 1)
    }
  }

  async function toggleProjectClosed(row) {
    if (!row?.id || actionBusyId) return
    setActionBusyId(row.id)
    setError('')
    try {
      if (row.isArchived || row.status === 'CLOSED') {
        await openProject(row.id)
      } else {
        await closeProject(row.id)
      }
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to update project state.'))
    } finally {
      setActionBusyId(null)
    }
  }

  async function exportProjectsToExcel() {
    // Build export payload — same filters as current list, stripped of pagination
    const exportFilters = { ...queryParams }
    delete exportFilters.page
    delete exportFilters.page_size
    delete exportFilters.includeTotal
    // triggerExport returns in < 20ms and polls for completion in the background
    await triggerExport(exportFilters)
  }

  return {
    state: {
      searchField,
      searchText,
      statusFilter,
      portfolioFilter,
      billableFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      leadFilter,
      priorityFilter,
      billingModeFilter,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      datePreset,
      isArchivedMode,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      stats,
      actionBusyId,
      canEditProject,
      canViewFinancials,
      profitabilityMap,
      profitabilityLoadingMap,
      searchFieldOptions: [
        { value: 'Name', label: 'Name' },
        { value: 'Code', label: 'Code' },
        { value: 'All', label: 'All' },
      ],
      companyOptions,
      branchOptions,
      departmentOptions,
      leadOptions,
      priorityOptions: [
        { value: 'All', label: 'All Priorities' },
        { value: 'HIGH', label: 'High' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'LOW', label: 'Low' },
      ],
      billingModeOptions: [
        { value: 'All', label: 'All Billing Modes' },
        { value: 'FIXED_PRICE', label: 'Fixed Price' },
        { value: 'AMC', label: 'AMC Revenue' },
        { value: 'TIME_AND_MATERIALS', label: 'Time & Materials' },
        { value: 'NON_BILLABLE', label: 'Non-Billable' },
      ],
      statusOptions: [
        { value: 'All', label: 'All Statuses' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'DRAFT', label: 'Draft' },
      ],
      portfolioOptions: [
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
        { value: 'all', label: 'All' },
      ],
      projectTypeFilter,
      projectTypeOptions: PROJECT_TYPE_OPTIONS,
    },
    actions: {
      setSearchField: (value) => updateParam('search_field', value),
      setSearchText: (value) => updateParam('search', value),
      setStatusFilter: (value) => updateParam('status', value),
      setPortfolioFilter: (value) => updateParam('portfolio', value),
      setProjectTypeFilter: (value) => updateParam('project_type', value),
      setBillableFilter: (value) => updateParam('billable', value),
      setCompanyFilter: (value) => updateParam('company_id', value),
      setBranchFilter: (value) => updateParam('branch_id', value),
      setDepartmentFilter: (value) => updateParam('department_id', value),
      setLeadFilter: (value) => updateParam('lead_id', value),
      setPriorityFilter: (value) => updateParam('priority', value),
      setBillingModeFilter: (value) => updateParam('billing_mode', value),
      setFromDate: (value) => updateParam('from_date', value),
      setToDate: (value) => updateParam('to_date', value),
      setDatePreset: (value) => updateParam('date_preset', value),
      onReset: () => setSearchParams(new URLSearchParams(), { replace: true }),
      goToPage,
      goToPreviousPage,
      goToNextPage,
      toggleProjectClosed,
      exportProjectsToExcel,
    },
  }
}
